import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaWhatsapp, FaEnvelope, FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import SEO from '../components/SEO/SEO';
import { fetchSEO } from '../store/slices/seoSlice';

const ContactPage = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settings.data);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappNumber = settings?.whatsappLink;
    const whatsappMessage = encodeURIComponent(
      `New Contact Form Submission:\n\n` +
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone || 'N/A'}\n` +
      `Subject: ${formData.subject}\n\n` +
      `Message:\n${formData.message}`
    );
    if (whatsappNumber) {
      window.open(`${whatsappNumber}?text=${whatsappMessage}`, '_blank');
      toast.success('Opening WhatsApp...');
      // Don't clear form data - keep it for user convenience
    } else {
      toast.error('WhatsApp link not configured');
    }
  };

  useEffect(() => {
    dispatch(fetchSEO('contact'));
  }, [dispatch]);

  return (
    <>
      <SEO
        page="contact"
        title="Contact Us - Get in Touch"
        description={settings?.joinOurJourney?.description || "Get in touch with us. We're here to help."}
        keywords="contact, get in touch, customer service"
        url="/contact"
      />
      {/* Hero Banner - Luxe Style */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <p className="text-xs font-sans font-medium uppercase tracking-[0.3em] text-primary mb-6">
            Contact
          </p>
          <h1 className="font-serif text-5xl md:text-6xl font-light text-gray-900 mb-6">
            Get in Touch
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            We'd love to hear from you
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
      <div className="max-w-6xl mx-auto">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="font-heading text-2xl font-bold text-black mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Subject *</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select a subject</option>
                  <option value="product-inquiry">Product Inquiry</option>
                  <option value="custom-order">Custom Order</option>
                  <option value="vastu-consultation">Vastu Consultation</option>
                  <option value="installation">Installation Service</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start gap-4">
                <FaEnvelope className="text-3xl text-primary mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Email</h3>
                  <p className="text-gray-600">{settings?.email || 'No email provided'}</p>
                  {settings?.supportEmail && (
                    <p className="text-gray-600">{settings.supportEmail}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start gap-4">
                <FaWhatsapp className="text-3xl text-green-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">WhatsApp</h3>
                  {settings?.whatsappNumber && (
                    <p className="text-gray-600 mb-3">{settings.whatsappNumber}</p>
                  )}
                  {settings?.whatsappLink && (
                    <a
                      href={settings.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                  >
                      <FaWhatsapp />
                      Chat on WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start gap-4">
                <FaMapMarkerAlt className="text-3xl text-primary mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Address</h3>
                  {settings?.address ? (
                    <p className="text-gray-600">
                      {settings.address.street && <>{settings.address.street}<br /></>}
                      {settings.address.city && <>{settings.address.city}{settings.address.state && `, ${settings.address.state}`} {settings.address.zipCode}<br /></>}
                      {settings.address.country || 'India'}
                    </p>
                  ) : (
                    <p className="text-gray-600">No address provided</p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-start gap-4">
                <FaClock className="text-3xl text-primary mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Business Hours</h3>
                  {settings?.businessHours?.weekdays && (
                    <p className="text-gray-600">{settings.businessHours.weekdays}</p>
                  )}
                  {settings?.businessHours?.weekend && (
                    <p className="text-gray-600">{settings.businessHours.weekend}</p>
                  )}
                  {!settings?.businessHours?.weekdays && !settings?.businessHours?.weekend && (
                    <p className="text-gray-600">No business hours provided</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default ContactPage;

